import { ArrowUp, Bell, MessageSquare, Search } from 'lucide-react'
import { Badge, Card, EmptyState, Notice, Skeleton, SkeletonGroup } from '@/components/play'
import {
  feedbackKindBadgeTone,
  feedbackKindLabels,
  feedbackPriorityBadgeTone,
  feedbackPriorityLabels,
  feedbackStatusBadgeTone,
  feedbackStatusLabels,
  formatRelativeTime,
} from '@/lib/feedback'
import type { FeedbackItemResource } from '@/types/feedback'

interface FeedbackListProps {
  items: FeedbackItemResource[]
  loading: boolean
  error?: string | null
}

/** Each item is a Play Card whose whole surface opens it: kind, status and priority, the title, an excerpt, and its
 *  votes, comments and followers. */
export function FeedbackList({ items, loading, error }: FeedbackListProps) {
  if (loading) {
    return (
      <SkeletonGroup label="Loading feedback" className="grid gap-3">
        {[1, 2, 3, 4].map((item) => (
          <Skeleton key={item} shape="panel" className="h-28" />
        ))}
      </SkeletonGroup>
    )
  }

  if (error) {
    return <Notice tone="error">{error}</Notice>
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Search />}
        title="No feedback here"
        description="Try another search or filter, or share feedback."
      />
    )
  }

  return (
    <div className="grid gap-3">
      {items.map((item) => {
        const { attributes } = item
        return (
          <Card
            key={item.id}
            as="article"
            title={attributes.title}
            action={{ to: `/feedback/${item.id}`, label: attributes.title }}
            actions={
              <>
                <Badge icon={<ArrowUp />}>
                  {attributes.votes_count}
                  <span className="sr-only"> votes</span>
                </Badge>
                <Badge icon={<MessageSquare />}>
                  {attributes.comments_count}
                  <span className="sr-only"> comments</span>
                </Badge>
                <Badge icon={<Bell />}>
                  {attributes.followers_count}
                  <span className="sr-only"> followers</span>
                </Badge>
              </>
            }
          >
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={feedbackKindBadgeTone(attributes.kind)}>{feedbackKindLabels[attributes.kind]}</Badge>
                <Badge tone={feedbackStatusBadgeTone(attributes.status)}>{feedbackStatusLabels[attributes.status]}</Badge>
                {attributes.priority ? (
                  <Badge tone={feedbackPriorityBadgeTone(attributes.priority)}>{feedbackPriorityLabels[attributes.priority]}</Badge>
                ) : null}
              </div>
              <p className="line-clamp-2 text-sm text-neutral-7">{attributes.description_markdown}</p>
              <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-7">
                <span>Reported by {attributes.reporter.name}</span>
                <span>Updated {formatRelativeTime(attributes.updated_at)}</span>
                {attributes.duplicate_of_title ? <span>Duplicate of {attributes.duplicate_of_title}</span> : null}
              </p>
            </div>
          </Card>
        )
      })}
    </div>
  )
}
